import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const AuthMagic = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = searchParams.get('token');
    const rawNext = searchParams.get('next') || '';
    const nextPath = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '';
    if (!token) {
      setError('Missing sign-in token.');
      return;
    }

    // Pick the post-login destination based on profile state and ?next= param.
    // - Profile incomplete → /complete-profile (preserving ?next so it routes
    //   onward after the name is set).
    // - Profile complete   → the requested next path if any, else /account.
    const pickDestination = (profileComplete) => {
      if (!profileComplete) {
        return nextPath ? `/complete-profile?next=${encodeURIComponent(nextPath)}` : '/complete-profile';
      }
      return nextPath || '/account';
    };

    // Survive React.StrictMode's double-mount: a token consumed by the first
    // mount must not be re-submitted by the second mount (it would 400).
    const seenKey = `magic-link-consumed:${token}`;
    if (sessionStorage.getItem(seenKey)) {
      // Already verified once in this tab — just resolve the auth state.
      checkAuth().finally(async () => {
        try {
          const { data } = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
          navigate(pickDestination(data?.profile_complete), { replace: true });
        } catch {
          navigate('/login', { replace: true });
        }
      });
      return;
    }

    const verify = async () => {
      try {
        const response = await axios.post(
          `${API_BASE}/auth/magic-link/verify`,
          { token },
          { withCredentials: true }
        );
        sessionStorage.setItem(seenKey, '1');

        setUser(response.data.user);
        await new Promise((r) => setTimeout(r, 100));
        await checkAuth();

        navigate(pickDestination(response.data.user?.profile_complete), { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'This sign-in link is invalid or has expired.');
      }
    };

    verify();
  }, [searchParams, navigate, setUser, checkAuth]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="magic-link-error">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 mb-4">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Sign-in link error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            data-testid="magic-link-back-to-login"
            onClick={() => navigate('/login', { replace: true })}
            className="w-full h-11 bg-gray-900 text-white hover:bg-gray-800"
          >
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen" data-testid="magic-link-verifying">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-gray-600 mx-auto mb-4" />
        <p className="text-gray-600">Signing you in…</p>
      </div>
    </div>
  );
};

export default AuthMagic;
